import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import api from "@/lib/api";
import { US_STATES } from "@/components/shared/StateCheckboxSelector";
import { reasonText } from "@/lib/ruleCheck";
import { ReceiptToggle } from "@/components/testleads/TestReceipt";

export const CASE_TYPES = [
  ["auto", "Car"], ["truck", "Truck"], ["motorcycle", "Motorcycle"], ["rideshare", "Rideshare"],
  ["pedestrian", "Pedestrian"], ["bicycle", "Bicycle"], ["other", "Other injury"],
];
// The buyer's own states come first, so a test lead matches by default.
export function StateOptions({ served, label = "code" }) {
  const mine = new Set(served || []);
  const opt = (s) => <option key={s.code} value={s.code}>{s[label]}</option>;
  if (!mine.size) return US_STATES.map(opt);
  return (
    <>
      <optgroup label="States they take">{US_STATES.filter((s) => mine.has(s.code)).map(opt)}</optgroup>
      <optgroup label="Other states (they don't take)">{US_STATES.filter((s) => !mine.has(s.code)).map(opt)}</optgroup>
    </>
  );
}

export const SELECT = "border-2 border-black px-1.5 py-1 text-[10px] uppercase tracking-widest bg-white";

const FUNNELS = [
  ["standard", "Standard"], ["quiz", "Quiz"], ["quiz-v2", "Quiz V2"], ["focused", "Focused"], ["auto", "Car"],
  ["truck", "Truck"], ["rideshare", "Rideshare"], ["motorcycle", "Motorcycle"], ["injury", "Personal Injury"],
];
const BTN = "border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1 disabled:opacity-40";

function resultText(r) {
  const att = r.attempts || [];
  const ok = att.filter((a) => a.status === "delivered").length;
  if (att.length && ok === att.length) return `Delivered to ${att.length === 1 ? att[0].destination_name : `all ${att.length} destinations`}.`;
  if (ok) return `Delivered to ${ok} of ${att.length} destinations.`;
  if (att.length) return "Not delivered — check the buyer's delivery settings.";
  if (r.turned_away?.length) return "Not delivered — this buyer's lead rules turned it away.";
  return `Lead ${r.status || "created"} but not delivered (${r.delivery_status || "no delivery attempt"}).`;
}

function AttemptLine({ a, testId }) {
  const ok = a.status === "delivered";
  const why = [a.response_code, a.response_body].filter(Boolean).join(": ");
  return (
    <li className={ok ? "text-emerald-800" : "text-red-800"} data-testid={testId}>
      {ok ? "OK" : "FAIL"} · {a.destination_name}{!ok && why ? ` — ${why}` : ""}
    </li>
  );
}

export function TestLeadResult({ r, showLeadLink, testId }) {
  const att = r.attempts || [];
  const ok = att.length > 0 && att.every((a) => a.status === "delivered");
  return (
    <div className={`border-2 p-2 text-[11px] ${ok ? "border-emerald-700 bg-emerald-50" : "border-amber-700 bg-amber-50"}`}
         data-testid={`${testId}-result`}>
      <span className="font-bold uppercase tracking-widest text-[10px]">TEST · {r.funnel_title}</span>{" "}
      <span data-testid={`${testId}-result-text`}>{resultText(r)}</span>
      {showLeadLink && (
        <Link to={`/leads/${r.lead_id}`} className="underline ml-1" data-testid={`${testId}-lead-link`}>Open lead</Link>
      )}
      {r.turned_away?.length > 0 && (
        <div className="mt-1 text-red-800" data-testid={`${testId}-turned-away`}>
          Real visitors with these answers won&apos;t reach this buyer either ({r.turned_away.map(reasonText).join(", ")}). Fix their lead rules on Manage Buyer.
        </div>
      )}
      {att.length > 1 && (
        <ul className="mt-1 space-y-0.5">
          {att.map((a, i) => <AttemptLine key={`${a.destination_id}-${i}`} a={a} testId={`${testId}-attempt-${i}`} />)}
        </ul>
      )}
      {att.map((a, i) => <ReceiptToggle key={`r-${a.destination_id}-${i}`} attempt={a} firm={r.firm_name} testId={`${testId}-receipt-${i}`} />)}
    </div>
  );
}

export const SendTestLeadButton = ({ url, body = {}, testId = "send-test-lead", confirmText, showLeadLink = false, onSent, funnels, states }) => {
  const options = funnels && funnels.length ? FUNNELS.filter(([v]) => funnels.includes(v)) : FUNNELS;
  const [funnel, setFunnel] = useState(options[0][0]);
  const [state, setState] = useState("");
  const [caseType, setCaseType] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    try {
      const { data } = await api.post(url, { ...body, funnel, ...(state ? { state } : {}), ...(caseType ? { case_type: caseType } : {}) });
      setResult(data);
      window.dispatchEvent(new Event("funnel-results:refresh"));
      onSent?.(data);
      toast.success(`Test lead sent via ${data.funnel_title}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the test lead");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <select value={funnel} onChange={(e) => setFunnel(e.target.value)} disabled={busy}
                className={SELECT} title="Page the test lead comes from"
                data-testid={`${testId}-funnel-select`}>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={state} onChange={(e) => setState(e.target.value)} disabled={busy} className={SELECT}
                title="Visitor's state" data-testid={`${testId}-state-select`}>
          <option value="">{states?.length ? "Any state they take" : "Any state"}</option>
          <StateOptions served={states} />
        </select>
        <select value={caseType} onChange={(e) => setCaseType(e.target.value)} disabled={busy} className={SELECT}
                title="Case type" data-testid={`${testId}-case-select`}>
          <option value="">Case type: auto</option>
          {CASE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button type="button" className={BTN} onClick={send} disabled={busy} data-testid={`${testId}-btn`}>
          <PaperPlaneTilt size={11} weight="bold" /> {busy ? "Sending…" : "Send test lead"}
        </button>
      </div>
      {result && <TestLeadResult r={result} showLeadLink={showLeadLink} testId={testId} />}
    </div>
  );
};
